import React, { useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Chip,
  Avatar,
  Typography,
  CircularProgress,
  TablePagination,
  Tooltip,
} from "@mui/material";
import { Edit, Trash2, Users } from "lucide-react";

const getStatusColor = (status) => {
  switch (status) {
    case "active":
      return { bg: "#ecfdf5", text: "#047857", label: "Active" };
    case "inactive":
      return { bg: "#fef3c7", text: "#b45309", label: "Inactive" };
    case "deleted":
      return { bg: "#fee2e2", text: "#dc2626", label: "Deleted" };
    default:
      return { bg: "#f3f4f6", text: "#6b7280", label: status };
  }
};
const formatRoleLabel = (role = "") => {
  if (!role) return "";

  return role
    .replace(/([a-z])([A-Z])/g, "$1 $2")
    .toLowerCase()
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

const getRoleColor = (role) => {
  switch (role) {
    case "superadmin":
      return { bg: "#fde68a", text: "#92400e" }; // gold
    case "admin":
      return { bg: "#e0e7ff", text: "#3730a3" }; // indigo
    case "accountManager":
      return { bg: "#cffafe", text: "#0e7490" }; // teal
    case "agent":
      return { bg: "#fce7f3", text: "#9d174d" }; // pink
    case "viewer":
      return { bg: "#f3f4f6", text: "#374151" }; // neutral grey
    case "manager":
      return { bg: "#dbeafe", text: "#1e40af" }; // blue
    default:
      return { bg: "#dcfce7", text: "#166534" }; // fallback green
  }
};

export default function UserTable({ users, loading, onEdit, onDelete }) {
  const role = localStorage.getItem("role");
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const paginatedUsers = users.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Paper
      sx={{
        borderRadius: "12px",
        background: "white",
        border: "1px solid #e5e7eb",
        boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
        overflow: "hidden",
        position: "relative",
      }}
    >
      {loading && (
        <Box
          sx={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: "rgba(255, 255, 255, 0.7)",
            zIndex: 10,
          }}
        >
          <CircularProgress />
        </Box>
      )}

      <TableContainer>
        <Table>
          <TableHead>
            <TableRow
              sx={{
                background: "#f9fafb",
                borderBottom: "2px solid #e5e7eb",
              }}
            >
              <TableCell sx={{ fontWeight: 600 }}>Name</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Email</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Role</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Status</TableCell>
              <TableCell sx={{ fontWeight: 600, textAlign: "center" }}>
                Actions
              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {paginatedUsers.map((user) => {
              const statusStyle = getStatusColor(user.status);
              const roleColor = getRoleColor(user.role);

              return (
                <TableRow
                  key={user._id}
                  sx={{ "&:hover": { background: "#f9fafb" } }}
                >
                  <TableCell>
                    <Box
                      sx={{ display: "flex", alignItems: "center", gap: 1.5 }}
                    >
                      <Avatar
                        sx={{
                          width: 30,
                          height: 30,
                          fontSize: "0.8rem",
                          background: "#6366f1",
                        }}
                      >
                        {user.fullName?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                      <Typography>{user.fullName}</Typography>
                    </Box>
                  </TableCell>

                  <TableCell>
                    <Typography sx={{ color: "#6b7280" }}>
                      {user.email}
                    </Typography>
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={formatRoleLabel(user.role)}
                      size="small"
                      sx={{
                        background: roleColor.bg,
                        color: roleColor.text,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>

                  <TableCell>
                    <Chip
                      label={statusStyle.label}
                      size="small"
                      sx={{
                        background: statusStyle.bg,
                        color: statusStyle.text,
                        fontWeight: 600,
                      }}
                    />
                  </TableCell>

                  <TableCell sx={{ textAlign: "center" }}>
                    {role === "admin" || role === "superadmin" ? (
                      <>
                        {/* ENABLED EDIT */}
                        <IconButton
                          onClick={() => onEdit(user)}
                          sx={{ color: "#3b82f6" }}
                        >
                          <Edit size={18} />
                        </IconButton>

                        {/* ENABLED DELETE */}
                        <IconButton
                          onClick={() => onDelete(user._id)}
                          sx={{ color: "#ef4444" }}
                        >
                          <Trash2 size={18} />
                        </IconButton>
                      </>
                    ) : (
                      <>
                        {/* DISABLED EDIT WITH TOOLTIP */}
                        <Tooltip
                          title="Permission denied"
                          placement="top"
                          arrow
                        >
                          <span>
                            <IconButton disabled sx={{ color: "#c3c3c4ff" }}>
                              <Edit size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>

                        {/* DISABLED DELETE WITH TOOLTIP */}
                        <Tooltip
                          title="Permission denied"
                          placement="top"
                          arrow
                        >
                          <span>
                            <IconButton disabled sx={{ color: "#bdbbbbff" }}>
                              <Trash2 size={18} />
                            </IconButton>
                          </span>
                        </Tooltip>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}

            {users.length === 0 && !loading && (
              <TableRow>
                <TableCell colSpan={5} align="center">
                  <Box sx={{ py: 4 }}>
                    <Users size={48} color="#d1d5db" />
                    <Typography>No users found</Typography>
                  </Box>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* ---------------------- Pagination Component ---------------------- */}
      <TablePagination
        component="div"
        rowsPerPageOptions={[5, 10, 25, 50]}
        count={users.length}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </Paper>
  );
}
